import { Anchor, Box, Text } from "@mantine/core"
import { ALL_VALUE } from "./constants.util"

export interface StylizeTextOptions {
	fw?: number | object
	fz?: number | object
	color?: string
	onAnchorClick?: () => void
	dataQaAttribute?: string
}

export function stylizeText(str: string, options?: StylizeTextOptions) {
	if (!str) return null

	const regex = /(\*[^*]+\*|\[[^\]]+\]|~[^~]+~)/g
	const parts = str.split(regex)

	return (
		<Box component="span">
			{parts.map((part: string, index: number) => {
				const uniqueKey = `styled-text-${index}-${part}`

				if (part.startsWith("*") && part.endsWith("*")) {
					return (
						<Text key={uniqueKey} fz={options?.fz} fw={options?.fw ?? 700} span>
							{part.replace(/\*/g, "")}
						</Text>
					)
				}

				if (part.startsWith("~") && part.endsWith("~")) {
					return (
						<Text
							key={uniqueKey}
							fz={options?.fz}
							fw={options?.fw}
							fs="italic"
							span
						>
							{part.replace(/~/g, "")}
						</Text>
					)
				}

				if (part.startsWith("[") && part.includes("]")) {
					const linkMatch = part.match(/\[([^\]]+)\]/)
					if (linkMatch) {
						const linkText = linkMatch[1]
						return (
							<Anchor
								key={uniqueKey}
								fz={options?.fz ?? 14}
								fw={options?.fw}
								underline="always"
								c={options?.color ?? "unset"}
								onClick={() =>
									options?.onAnchorClick ? options?.onAnchorClick() : null
								}
								data-qa={options?.dataQaAttribute}
							>
								{linkText}
							</Anchor>
						)
					}
				}

				return (
					<Text fz={options?.fz} fw={options?.fw} key={uniqueKey} span>
						{part}
					</Text>
				)
			})}
		</Box>
	)
}

export const capitalizeWords = (value: string): string => {
	const exceptions = new Map([
		["ny", "NY"],
		["nj", "NJ"],
		["fl", "FL"],
		["usa", "USA"],
		["qld", "QLD"]
	])

	const capitalizePart = (part: string): string => {
		if (/^([a-z]\.)+$/i.test(part)) {
			return part
				.split(".")
				.map((subPart) => subPart.toUpperCase())
				.join(".")
		}

		const lowerPart = part?.toLowerCase()
		if (exceptions.has(lowerPart)) {
			return exceptions.get(lowerPart) || part
		}
		return part[0]?.toUpperCase() + part.slice(1)?.toLowerCase()
	}

	return value
		.split(/(\s|\/|[(),-])/g)
		.map((part) => {
			const trimmed = part.trim()
			return /[a-zA-Z]/.test(trimmed) ? capitalizePart(trimmed) : part
		})
		.join("")
}

export const alphabeticalSort = <T extends { label: string; value: string }>(
	array: T[]
) => {
	const allElement = array.find((item) => item.value === ALL_VALUE)
	const filteredArray = array
		.filter((item) => item.value !== ALL_VALUE)
		.map((item) => ({
			...item,
			label: capitalizeWords(item.label)
		}))

	const sortedArray = filteredArray.sort((a, b) =>
		a.label.localeCompare(b.label)
	)

	const finalArray = allElement ? [allElement, ...sortedArray] : sortedArray
	return finalArray
}
